import { MessageAttachment } from '../types/ai';
import { FileText, Image, Video, Music, Archive, File, FileSpreadsheet } from 'lucide-react';

interface MessageAttachmentsProps {
  attachments: MessageAttachment[];
}

export function MessageAttachments({ attachments }: MessageAttachmentsProps) {
  const getFileIcon = (type: string, name?: string) => {
    if (type.startsWith('image/')) return <Image className="w-4 h-4" />;
    if (type.startsWith('video/')) return <Video className="w-4 h-4" />;
    if (type.startsWith('audio/')) return <Music className="w-4 h-4" />;
    if (type.includes('pdf')) return <FileText className="w-4 h-4" />;
    if (type.includes('zip') || type.includes('rar')) return <Archive className="w-4 h-4" />;
    if (type.includes('spreadsheet') || name?.endsWith('.xlsx') || name?.endsWith('.xls') || name?.endsWith('.csv')) {
      return <FileSpreadsheet className="w-4 h-4" />;
    }
    return <File className="w-4 h-4" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const isImage = (type: string) => type.startsWith('image/');

  const getImagePreview = (attachment: MessageAttachment): string | null => {
    if (!isImage(attachment.type)) return null;
    
    // Se tem URL permanente, usar URL
    if (attachment.url) return attachment.url;
    
    // Se tem File object, criar preview local temporário
    if (attachment.file) {
      try {
        return URL.createObjectURL(attachment.file);
      } catch (error) {
        console.error('Erro ao criar preview da imagem:', error);
        return null;
      }
    }
    
    // Se não tem nem URL nem File, não mostrar preview
    return null;
  };

  return (
    <div className="space-y-2 mt-2">
      {attachments.map((attachment, index) => {
        const imagePreview = getImagePreview(attachment);
        
        if (imagePreview) {
          // Exibir imagem com preview
          return (
            <div key={index} className="relative group">
              <img
                src={imagePreview}
                alt={attachment.name}
                className="max-w-xs max-h-64 rounded-lg border border-gray-200 shadow-sm"
                onLoad={(e) => {
                  // Revogar URL do objeto quando a imagem carregar (para evitar memory leak)
                  if (attachment.file && !attachment.url) {
                    setTimeout(() => {
                      URL.revokeObjectURL((e.target as HTMLImageElement).src);
                    }, 100);
                  }
                }}
              />
              <div className="mt-1 text-xs text-gray-500">
                {attachment.name} • {formatFileSize(attachment.size)}
              </div>
            </div>
          );
        } else {
          // Exibir arquivo genérico (ou imagem sem preview)
          const isImageFile = isImage(attachment.type);
          
          return (
            <div
              key={index}
              className={`flex items-center space-x-3 border rounded-lg px-3 py-2 max-w-xs ${
                isImageFile 
                  ? 'bg-blue-50 border-blue-200' 
                  : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex-shrink-0 text-gray-500">
                {getFileIcon(attachment.type, attachment.name)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {attachment.name}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(attachment.size)}
                  {isImageFile && ' • Imagem enviada'}
                </p>
              </div>
            </div>
          );
        }
      })}
    </div>
  );
}
